import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { mondayGraphQL } from '../../lib/monday'
import {
    FaTrash, FaSignOutAlt, FaSearch, FaImage, FaTimes,
    FaCheckCircle, FaExclamationCircle, FaInfoCircle,
    FaHdd, FaLink, FaExternalLinkAlt, FaFilePdf, FaDownload,
    FaDatabase, FaArrowLeft
} from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { RoleProvider, useRole } from '../../hooks/useRole'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import ServiceHealthBar from '../../components/ServiceHealthBar'

export default function StorageManagementContainer() {
    const [session, setSession] = useState(null)

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => setSession(session))
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session))
        return () => subscription.unsubscribe()
    }, [])

    if (!session) return <StorageManagementContent session={session} setSession={setSession} />

    return (
        <RoleProvider session={session}>
            <StorageManagementContent session={session} setSession={setSession} />
        </RoleProvider>
    )
}

function StorageManagementContent({ session, setSession }) {
    const { t } = useTranslation()
    const { permissions, loading: roleLoading } = useRole()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [buckets, setBuckets] = useState([])
    const [files, setFiles] = useState([])
    const [usageData, setUsageData] = useState({ posts: [], storeItems: [], portfolios: [] })
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedBucket, setSelectedBucket] = useState('all')

    // Auth state
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [authLoading, setAuthLoading] = useState(false)
    const [honeyPot, setHoneyPot] = useState('')

    // Toast state
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' })
    const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null })

    // Backup state
    const [backupProgress, setBackupProgress] = useState('')

    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type })
        setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000)
    }

    const showConfirm = (message, onConfirm) => {
        setConfirmDialog({ show: true, message, onConfirm })
    }

    // Auth logic (replicated from AdminDashboard)
    // Wait for auth and roles to initialize
    useEffect(() => {
        if (!roleLoading && permissions['admin.storage.access']) {
            fetchAllData()
        } else if (!roleLoading && !permissions['admin.storage.access']) {
            setLoading(false)
        }
    }, [permissions, roleLoading])

    const markStorageRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible (guard during backup)
    const { markRefreshed: markStorageRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (!permissions['admin.storage.access'] || backupProgress) {
                markStorageRefreshedRef.current?.()
                return
            }
            Promise.resolve(fetchAllData(true)).finally(() => markStorageRefreshedRef.current?.())
        }, [permissions, backupProgress]),
    })
    markStorageRefreshedRef.current = markStorageRefreshed

    const handleLogin = async (e) => {
        e.preventDefault()
        if (honeyPot) return
        setAuthLoading(true)
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password })
            if (error) throw error
            showToast(t('auth.success'), 'success')
        } catch (error) {
            showToast(error.message, 'error')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleLogout = async () => {
        await supabase.auth.signOut()
        setSession(null)
        setFiles([])
    }

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true)
        try {
            // Fetch usage references
            const [postsRes, storeRes, portfolioRes] = await Promise.all([
                supabase.from('posts').select('id, title_en, title_es, image_url, content_en, content_es'),
                supabase.from('store_items').select('id, title_en, title_es, image_url, image_gallery'),
                supabase.from('portfolios').select('id, business_unit, file_path')
            ])

            setUsageData({
                posts: postsRes.data || [],
                storeItems: storeRes.data || [],
                portfolios: portfolioRes.data || []
            })

            // Fetch files from buckets
            const bucketList = ['blog-images', 'store-images', 'portfolios']
            setBuckets(bucketList)

            const allFiles = []
            for (const bucket of bucketList) {
                const { data, error } = await supabase.storage.from(bucket).list('', {
                    limit: 100,
                    offset: 0,
                    sortBy: { column: 'name', order: 'desc' }
                })

                if (data) {
                    const filesWithUrls = data.map(file => {
                        const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(file.name)
                        return { ...file, bucket, publicUrl }
                    })
                    allFiles.push(...filesWithUrls)
                }
            }
            setFiles(allFiles)
        } catch (err) {
            showToast('Error loading data: ' + err.message, 'error')
        } finally {
            if (!silent) setLoading(false)
        }
    }

    const handleBackup = async () => {
        setLoading(true)
        try {
            const backupData = { supabase_tables: {}, supabase_storage: {}, monday_boards: {}, edge_functions: {} }

            // ── 1. Supabase Tables ──────────────────────────────────
            setBackupProgress('Backing up database tables...')
            const tables = [
                'posts', 'store_items', 'portfolios', 'user_roles', 'catalog_items',
                'estimates',
                'arch_estimates', 'arch_config', 'arch_difficulty_levels', 'arch_terms_conditions',
                'arch_addons', 'arch_catalog_items', 'arch_payment_info', 'arch_schedule_presets',
                'arch_add_alt_difficulty_options', 'arch_civil_difficulty_options',
                'project_start_dates', 'service_galleries', 'gallery_images', 'role_templates',
                'workflow_stage_timers', 'sub_service_groups', 'sub_services',
                'categories', 'price_groups', 'catalog_item_prices',
                'arch_cost_roles', 'arch_discipline_presets', 'arch_scope_multipliers'
            ]
            for (const table of tables) {
                const { data, error } = await supabase.from(table).select('*')
                if (error) throw error
                backupData.supabase_tables[table] = data
            }

            // ── 2. Supabase Storage (files as base64) ───────────────
            const BUCKETS = ['blog-images', 'store-images', 'portfolios']
            for (const bucket of BUCKETS) {
                setBackupProgress(`Downloading ${bucket}...`)
                try {
                    const { data: fileList, error: listErr } = await supabase.storage.from(bucket).list('', { limit: 1000, sortBy: { column: 'name', order: 'asc' } })
                    if (listErr) throw listErr

                    const bucketFiles = []
                    for (const file of (fileList || [])) {
                        if (!file.name || file.id === null) continue
                        try {
                            const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(file.name)
                            const res = await fetch(publicUrl)
                            if (!res.ok) { bucketFiles.push({ name: file.name, error: `HTTP ${res.status}` }); continue }
                            const arrayBuf = await res.arrayBuffer()
                            const bytes = new Uint8Array(arrayBuf)
                            let binary = ''
                            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
                            const base64 = btoa(binary)
                            bucketFiles.push({
                                name: file.name,
                                size: bytes.length,
                                mimeType: res.headers.get('content-type') || 'application/octet-stream',
                                base64
                            })
                        } catch (dlErr) {
                            bucketFiles.push({ name: file.name, error: dlErr.message })
                        }
                    }
                    backupData.supabase_storage[bucket] = { files: bucketFiles, count: bucketFiles.length }
                } catch (err) {
                    console.error(`Storage backup error (${bucket}):`, err)
                    backupData.supabase_storage[bucket] = { error: err.message, count: 0 }
                }
            }

            // ── 3. Monday.com Boards ────────────────────────────────
            setBackupProgress('Backing up Monday.com boards...')
            const BOARDS = [
                { id: '18157237865', name: 'Construction' },
                { id: '18396590426', name: 'Architecture' },
                { id: '18396594554', name: 'Project Miami 2026' },
                { id: '18396592406', name: 'Project Dallas 2026' },
            ]

            for (const board of BOARDS) {
                try {
                    const structData = await mondayGraphQL(`
                        query($id: [ID!]) {
                            boards(ids: $id) {
                                id name
                                columns { id title type settings_str }
                                groups { id title }
                            }
                        }
                    `, { id: [board.id] })

                    const boardInfo = structData.boards[0]

                    let allItems = []
                    let cursor = null
                    do {
                        const page = await mondayGraphQL(`
                            query($boardId: ID!, $limit: Int!, $cursor: String) {
                                boards(ids: [$boardId]) {
                                    items_page(limit: $limit, cursor: $cursor) {
                                        cursor
                                        items { id name group { id title } column_values { id type text value } }
                                    }
                                }
                            }
                        `, { boardId: board.id, limit: 100, cursor })
                        const pg = page.boards[0].items_page
                        allItems = allItems.concat(pg.items)
                        cursor = pg.cursor
                    } while (cursor)

                    backupData.monday_boards[board.id] = {
                        ...boardInfo,
                        items: allItems,
                        itemCount: allItems.length
                    }
                } catch (err) {
                    console.error(`Monday backup error (${board.name}):`, err)
                    backupData.monday_boards[board.id] = { name: board.name, error: err.message }
                }
            }

            // ── 4. Edge Function Metadata ────────────────────────────
            setBackupProgress('Cataloging edge functions...')
            backupData.edge_functions = {
                project_ref: 'czcwsxyxfsxzwyvnziof',
                functions: [
                    { name: 'sitemap', jwt: false, purpose: 'XML sitemap with blog posts' },
                    { name: 'health', jwt: false, purpose: 'Keep-alive ping (UptimeRobot)' },
                    { name: 'translate-post', jwt: false, purpose: 'Blog translation via ZhipuAI' },
                    { name: 'monday-upload', jwt: false, purpose: 'File upload proxy (Monday CORS fix)' },
                    { name: 'stripe-proxy', jwt: false, purpose: 'Stripe API proxy (products, prices, payment links)' },
                    { name: 'ghl-proxy', jwt: false, purpose: 'GoHighLevel API proxy (contacts, opportunities, ping)' },
                    { name: 'ghl-webhook', jwt: false, purpose: 'GHL incoming webhook handler' },
                    { name: 'monday-proxy', jwt: false, purpose: 'Monday.com API proxy (CORS fix)' },
                    { name: 'drive-proxy', jwt: false, purpose: 'Google Drive API proxy' },
                    { name: 'monday-webhook', jwt: false, purpose: 'Monday.com webhook handler' },
                    { name: 'places-proxy', jwt: false, purpose: 'Google Places API proxy' },
                    { name: 'user-admin', jwt: false, purpose: 'User admin / role management' },
                ],
                secrets_required: ['MONDAY_API_KEY', 'STRIPE_SECRET_KEY', 'GLM_API_KEY', 'GHL_KEY_DALLAS', 'GHL_KEY_MIAMI', 'GHL_KEY_CONSTRUCTION', 'GOOGLE_DRIVE_SA_KEY', 'GOOGLE_DRIVE_API_KEY', 'SUPABASE_URL', 'SUPABASE_ANON_KEY', 'SUPABASE_DB_URL', 'SUPABASE_SERVICE_ROLE_KEY'],
                deploy_command: 'npx supabase functions deploy --no-verify-jwt',
                note: 'Source code is in git under supabase/functions/. This is metadata only.'
            }

            // ── Metadata ────────────────────────────────────────────
            backupData.__metadata = {
                timestamp: new Date().toISOString(),
                version: '3.0',
                source: 'JD Rivero Storage Management (UI)',
                coverage: {
                    supabase_tables: tables,
                    supabase_storage: BUCKETS,
                    monday_boards: BOARDS.map(b => `${b.name} (${b.id})`),
                    edge_functions: '12 functions cataloged'
                }
            }

            // ── Download ────────────────────────────────────────────
            setBackupProgress('Generating backup file...')
            const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `jdrivero_full_backup_${new Date().toISOString().split('T')[0]}.json`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            showToast(t('admin.storage.backup_success'), 'success')
        } catch (err) {
            showToast(t('admin.storage.backup_error') + err.message, 'error')
        } finally {
            setLoading(false)
            setBackupProgress('')
        }
    }

    const getFileUsage = (file) => {
        const usage = []
        const url = file.publicUrl
        const path = file.name

        // Check posts
        usageData.posts.forEach(post => {
            if (post.image_url === url || post.content_en?.includes(url) || post.content_es?.includes(url)) {
                usage.push({ type: 'Blog', title: post.title_en || post.title_es || 'Untitled Post', id: post.id })
            }
        })

        // Check store items
        usageData.storeItems.forEach(item => {
            const gallery = item.image_gallery || []
            if (item.image_url === url || gallery.includes(url)) {
                usage.push({ type: 'Store', title: item.title_en || item.title_es || 'Untitled Item', id: item.id })
            }
        })

        // Check portfolios
        usageData.portfolios.forEach(p => {
            if (p.file_path === path || p.file_path?.includes(path)) {
                usage.push({ type: 'Portfolio', title: p.business_unit, id: p.id })
            }
        })

        return usage
    }

    const handleDeleteFile = async (file) => {
        showConfirm(`Are you sure you want to delete ${file.name}?`, async () => {
            try {
                const pathsToDelete = [file.name]
                const bucket = supabase.storage.from(file.bucket)

                // Detect folder: 0 bytes, no extension, or trailing slash
                const isFolder = file.name.endsWith('/') ||
                    ((file.metadata?.size || 0) === 0 && !file.name.includes('.'))

                if (isFolder) {
                    const folderPath = file.name.endsWith('/') ? file.name.slice(0, -1) : file.name

                    // Recursively list all objects under this prefix
                    const listAll = async (prefix) => {
                        const items = []
                        let offset = 0
                        const limit = 1000
                        while (true) {
                            const { data, error } = await bucket.list(prefix, { limit, offset })
                            if (error) {
                                console.warn('[StorageManagement] list error:', error)
                                break
                            }
                            if (!data || data.length === 0) break
                            for (const item of data) {
                                const itemPath = prefix ? `${prefix}/${item.name}` : item.name
                                items.push(itemPath)
                                // If item looks like a nested folder, recurse
                                const isNested = (item.metadata?.size || 0) === 0 && !item.name.includes('.')
                                if (isNested && item.name !== prefix) {
                                    const nested = await listAll(itemPath)
                                    items.push(...nested)
                                }
                            }
                            if (data.length < limit) break
                            offset += limit
                        }
                        return items
                    }

                    const contents = await listAll(folderPath)
                    pathsToDelete.push(...contents)
                }

                // Remove in batches of 100 (Supabase remove limit)
                for (let i = 0; i < pathsToDelete.length; i += 100) {
                    const batch = pathsToDelete.slice(i, i + 100)
                    const { error } = await bucket.remove(batch)
                    if (error) throw error
                }

                setFiles(prev => prev.filter(f => !(f.name === file.name && f.bucket === file.bucket)))
                showToast(t('admin.storage.delete_success'), 'success')
            } catch (err) {
                console.error('[StorageManagement] delete error:', err)
                showToast(t('admin.storage.delete_error') + ': ' + err.message, 'error')
            }
        })
    }

    const formatSize = (bytes) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const totalUsedSpace = files.reduce((acc, file) => acc + (file.metadata?.size || 0), 0)
    const QUOTA = 1024 * 1024 * 1024 // 1GB assumed for free tier
    const usagePercent = Math.min((totalUsedSpace / QUOTA) * 100, 100)

    const filteredFiles = files.filter(file => {
        const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesBucket = selectedBucket === 'all' || file.bucket === selectedBucket
        return matchesSearch && matchesBucket
    })

    if (!session) {
        return (
            <div className="flex justify-center items-center p-4 min-h-screen bg-cloud">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                    <div className="p-8 bg-white rounded-2xl border shadow-2xl">
                        <h2 className="mb-6 text-3xl font-bold text-center text-primary">{t('auth.login')}</h2>
                        <form onSubmit={handleLogin} className="space-y-6">
                            <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                                <input type="text" value={honeyPot} onChange={(e) => setHoneyPot(e.target.value)} tabIndex="-1" autoComplete="off" />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium text-ash">{t('auth.email')}</label>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="px-4 py-3 w-full rounded-lg border outline-none bg-cloud focus:ring-2 focus:ring-primary" required />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium text-ash">{t('auth.password')}</label>
                                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="px-4 py-3 w-full rounded-lg border outline-none bg-cloud focus:ring-2 focus:ring-primary" required />
                            </div>
                            <button type="submit" disabled={authLoading} className="flex gap-2 justify-center items-center py-3 w-full font-bold text-white rounded-lg transition bg-primary hover:shadow-lg">
                                {authLoading ? <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white"></div> : t('auth.signIn')}
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        )
    }

    if (roleLoading) {
        return (
            <div className="flex justify-center items-center p-8 min-h-screen bg-cloud">
                <div className="mx-auto w-12 h-12 rounded-full border-b-2 animate-spin border-primary"></div>
            </div>
        )
    }

    if (!permissions['admin.storage.access']) {
        return (
            <div className="flex flex-col justify-center items-center p-8 space-y-4 min-h-screen text-center bg-cloud">
                <FaExclamationCircle className="text-5xl text-red-500" />
                <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                <p className="text-ash">{t('admin.access.req_super_admin')}</p>
                <button onClick={() => navigate('/admin')} className="flex gap-2 justify-center items-center px-6 py-2 font-medium rounded-lg shadow transition-all bg-primary text-ink hover:bg-primary/90">
                    <FaArrowLeft />
                    {t('common.return_dashboard')}
                </button>
            </div>
        )
    }

    return (
        <div className="overflow-x-clip pb-12 min-h-screen bg-cloud">
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link
                        to="/admin"
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all text-ash shrink-0"
                        title={t('admin.storage.return', 'Return')}
                    >
                        <FaArrowLeft />
                    </Link>
                    <h1 className="text-xl font-bold text-primary flex-1">{t('admin.storage.title')}</h1>
                    <button
                        onClick={handleBackup}
                        disabled={!!backupProgress}
                        className="px-3 py-1.5 text-sm font-semibold text-white rounded-lg transition flex items-center gap-2 bg-ink hover:bg-ink/90 disabled:opacity-50 disabled:cursor-wait shrink-0"
                    >
                        <FaDownload /> <span className="hidden sm:inline">{backupProgress || t('admin.storage.backup', 'Full Backup')}</span>
                    </button>
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-lg border border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all text-ash shrink-0"
                        title={t('auth.signOut', 'Sign Out')}
                    >
                        <FaSignOutAlt />
                    </button>
                </div>
                <div className="max-w-7xl mx-auto px-4 pb-2">
                    <ServiceHealthBar />
                </div>
            </header>

            <main className="px-4 py-8 mx-auto max-w-7xl">
                {/* Stats Card */}
                <div className="grid gap-8 items-center p-6 mb-8 bg-white rounded-2xl border shadow-sm md:grid-cols-3">
                    <div>
                        <div className="flex gap-3 items-center mb-2">
                            <FaHdd className="text-xl text-primary" />
                            <h3 className="font-bold text-ink">{t('admin.storage.capacity')}</h3>
                        </div>
                        <p className="text-2xl font-black text-primary">{formatSize(totalUsedSpace)} <span className="text-sm font-normal text-ash">of {formatSize(QUOTA)}</span></p>
                    </div>
                    <div className="md:col-span-2">
                        <div className="flex justify-between mb-2 text-sm font-bold">
                            <span className="text-ink">{t('admin.storage.usage')}</span>
                            <span className={usagePercent > 80 ? 'text-red-500' : 'text-primary'}>{usagePercent.toFixed(1)}%</span>
                        </div>
                        <div className="overflow-hidden h-4 bg-gray-100 rounded-full border">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${usagePercent}%` }}
                                className={`h-full ${usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-amber-500' : 'bg-primary'}`}
                            />
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-4 mb-8 md:flex-row">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-ash" />
                        <input
                            type="text"
                            placeholder="Search files..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="py-3 pr-4 pl-12 w-full bg-white rounded-xl border shadow-sm transition outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                    <div className="flex gap-2 p-1 bg-white rounded-xl border shadow-sm">
                        <button
                            onClick={() => setSelectedBucket('all')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition ${selectedBucket === 'all' ? 'bg-primary text-white shadow-md' : 'text-ash hover:bg-cloud'}`}
                        >
                            All
                        </button>
                        {buckets.map(b => (
                            <button
                                key={b}
                                onClick={() => setSelectedBucket(b)}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition capitalize ${selectedBucket === b ? 'bg-primary text-white shadow-md' : 'text-ash hover:bg-cloud'}`}
                            >
                                {b.split('-')[0]}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col gap-4 justify-center items-center py-20">
                        <div className="w-10 h-10 rounded-full border-4 animate-spin border-primary/30 border-t-primary" />
                        <p className="font-medium text-ash">Scanning Buckets...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {filteredFiles.map((file, idx) => {
                            const usage = getFileUsage(file)
                            const isUnused = usage.length === 0
                            const isImage = file.metadata?.mimetype?.startsWith('image/') || file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                            const isPdf = file.metadata?.mimetype === 'application/pdf' || file.name.endsWith('.pdf')

                            return (
                                <motion.div
                                    key={`${file.bucket}-${file.name}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05 }}
                                    className="overflow-hidden bg-white rounded-2xl border shadow-sm transition-all hover:shadow-xl group"
                                >
                                    {/* Preview */}
                                    <div className="flex overflow-hidden relative justify-center items-center aspect-video bg-cloud">
                                        {isImage ? (
                                            <img src={file.publicUrl} alt={file.name} className="object-cover w-full h-full transition duration-500 group-hover:scale-110" />
                                        ) : isPdf ? (
                                            <FaFilePdf className="text-6xl text-red-500" />
                                        ) : (
                                            <FaImage className="text-6xl text-gray-200" />
                                        )}
                                        <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold rounded uppercase tracking-wider">
                                            {file.bucket.split('-')[0]}
                                        </div>
                                        <div className="flex absolute top-2 right-2 gap-1">
                                            <a href={file.publicUrl} target="_blank" rel="noreferrer" className="p-2 rounded-lg shadow-sm opacity-0 transition bg-white/90 hover:bg-white text-ink group-hover:opacity-100">
                                                <FaExternalLinkAlt className="text-sm" />
                                            </a>
                                            <button
                                                onClick={() => handleDeleteFile(file)}
                                                className="p-2 text-white rounded-lg shadow-sm opacity-0 transition bg-red-500/90 hover:bg-red-500 group-hover:opacity-100"
                                            >
                                                <FaTrash className="text-sm" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="p-4 space-y-3">
                                        <div className="flex gap-2 justify-between items-start">
                                            <p className="flex-1 text-xs font-bold truncate text-ink" title={file.name}>{file.name}</p>
                                            <p className="text-[10px] font-medium text-ash whitespace-nowrap">{formatSize(file.metadata?.size || 0)}</p>
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-bold text-ash uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                                <FaLink className="text-[9px]" /> Used In
                                            </p>
                                            <div className="space-y-1">
                                                {isUnused ? (
                                                    <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full border border-amber-100">
                                                        Unused / Orphaned
                                                    </span>
                                                ) : (
                                                    usage.map((u, i) => (
                                                        <div key={i} className="flex items-center gap-1 text-[10px] text-ink font-medium">
                                                            <span className="w-1 h-1 rounded-full bg-primary" />
                                                            <span className="text-ash uppercase font-bold text-[8px]">{u.type}:</span>
                                                            <span className="truncate">{u.title}</span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>
                )}
            </main>

            {/* Custom Dialogs */}
            <AnimatePresence>
                {confirmDialog.show && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 backdrop-blur-sm bg-ink/40" onClick={() => setConfirmDialog({ ...confirmDialog, show: false })} />
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="relative p-8 w-full max-w-sm text-center bg-white rounded-2xl shadow-2xl">
                            <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 text-3xl text-red-500 bg-red-50 rounded-full"><FaTrash /></div>
                            <h3 className="mb-2 text-xl font-bold text-ink">{t('admin.storage.confirm_delete')}</h3>
                            <p className="mb-6 text-sm text-ash">{confirmDialog.message}</p>
                            <div className="flex gap-3">
                                <button onClick={() => setConfirmDialog({ ...confirmDialog, show: false })} className="flex-1 py-3 font-bold rounded-xl border-2 border-gray-100 transition hover:bg-gray-50">{t('common.cancel')}</button>
                                <button
                                    onClick={() => {
                                        confirmDialog.onConfirm()
                                        setConfirmDialog({ ...confirmDialog, show: false })
                                    }}
                                    className="flex-1 py-3 font-bold text-white bg-red-500 rounded-xl shadow-lg transition hover:bg-red-600 shadow-red-500/20"
                                >
                                    Delete
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {toast.show && (
                    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed bottom-8 right-8 z-[70]">
                        <div className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border backdrop-blur-sm ${toast.type === 'success' ? 'bg-green-50 text-green-900 border-green-200' :
                            toast.type === 'error' ? 'bg-red-50 text-red-900 border-red-200' :
                                'bg-blue-50 text-blue-900 border-blue-200'
                            }`}>
                            {toast.type === 'success' && <FaCheckCircle className="text-xl" />}
                            {toast.type === 'error' && <FaExclamationCircle className="text-xl" />}
                            {toast.type === 'info' && <FaInfoCircle className="text-xl" />}
                            <span className="font-medium">{toast.message}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
